<template>
  <div class="card p-shadow-app">
    <h1>Categorias</h1>
    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />
    <ConfirmDialog></ConfirmDialog>
    <div style="height: 65vh">
      <TreeTable
        :value="treeValue"
        :filters.sync="filters"
        :loading="loading"
        class="p-treetable-sm"
        responsiveLayout="scroll"
        :scrollable="true"
        scrollHeight="flex"
      >
        <template #empty> Nada a mostrar. </template>
        <template #loading> A carregar. Por favor Aguarde... </template>
        <template #header>
          <div class="table-header">
            <Button
              icon="pi pi-plus"
              @click="addNew"
              v-if="can('createUpdateParts')"
              class="p-mr-2"
            />
            <Button
              icon="pi pi-sync"
              label="Sincronizar de PrestaShop"
              @click="importCategories"
              v-if="can('createUpdateParts')"
              :disabled="importBatchActive"
              class="p-button-secondary"
              v-tooltip="'Importar/Atualizar categorias vindas do PrestaShop'"
            />
            <span
              v-if="importBatch.status"
              class="import-status p-ml-2"
              :class="importBatchStatusClass"
              v-tooltip="'Clique para atualizar o estado da importação'"
              @click="loadImportBatchStatus"
            >
              <i
                class="pi p-mr-1"
                :class="
                  importBatch.status.cancelled
                    ? 'pi-ban'
                    : importBatch.status.finished
                    ? importBatch.status.failed_jobs > 0
                      ? 'pi-exclamation-triangle'
                      : 'pi-check-circle'
                    : 'pi-spin pi-spinner'
                "
              ></i>
              {{ importBatchStatusLabel }}
            </span>
            <Button
              v-if="importBatchActive && can('createUpdateParts')"
              icon="pi pi-stop-circle"
              class="p-button-danger p-button-sm p-ml-1"
              v-tooltip="'Cancelar importação de categorias'"
              :loading="importBatch.cancelling"
              @click="confirmCancelImportBatch"
            />
          </div>
        </template>
        <Column
          field="name"
          header="Nome da Categoria"
          :expander="true"
          sortable
        >
          <template #body="slotProps">
            <span
              class="cursor-pointer"
              v-tooltip="'Alterar Nome da Categoria'"
              @click="edit(slotProps.node.data)"
              v-if="can('createUpdateParts')"
            >
              <i class="pi pi-pencil p-mr-2"></i>
              {{ slotProps.node.data.name + (slotProps.node.data.api_id ? ' (#' + String(slotProps.node.data.api_id) + ')' : '') }}
            </span>
            <span v-else>{{ slotProps.node.data.name + (slotProps.node.data.api_id ? ' (#' + String(slotProps.node.data.api_id) + ')' : '') }}</span>
          </template>
          <template #filter>
            <InputText
              type="text"
              v-model="filters['name']"
              class="p-column-filter p-inputtext-sm"
              placeholder="Filtrar por nome"
            />
          </template>
        </Column>
        <Column field="parent_id" header="Categoria-Pai">
          <template #body="slotProps">
            {{ getParentName(slotProps.node.data.parent_id) }}
          </template>
        </Column>
        <Column field="description" header="Descrição">
          <template #body="slotProps">
            {{ slotProps.node.data.description ?? "" }}
          </template>
        </Column>
        <Column field="active" header="Activa">
          <template #body="slotProps">
            {{ slotProps.node.data.active == 1 ? "Sim" : "Não" }}
          </template>
        </Column>
        <Column header="Ações" v-if="can('createUpdateParts')" :style="{ width: '100px' }">
          <template #body="slotProps">
            <Button
              icon="pi pi-trash"
              class="p-button-rounded p-button-danger p-button-text"
              @click="confirmDelete(slotProps.node.data)"
              v-tooltip="'Eliminar Categoria'"
            />
          </template>
        </Column>
      </TreeTable>
    </div>
    <Dialog
      :visible.sync="form.show"
      :containerStyle="{ width: $isMobile() ? '100vw' : '40vw' }"
      :maximizable="true"
      :modal="true"
      v-on:hide="hideForm"
      v-on:show="
        () => {
          $validator.resume();
        }
      "
      :header="
        form.id
          ? 'Editar Categoria'
          : 'Nova Categoria'
      "
    >
      <form class="p-col-12" @submit.prevent="save">
        <div class="p-fluid p-formgrid p-grid">
          <div class="p-field p-col-12">
            <span class="p-float-label">
              <InputText
                :id="'name'"
                :name="'name'"
                autocomplete="off"
                v-validate="'required'"
                v-model="form.name"
                v-bind:class="[
                  {
                    'p-invalid': errors.has('name'),
                  },
                  'form-control',
                ]"
              />
              <label for="name">Nome da Categoria</label>
            </span>
            <small v-if="errors.has('name')" class="p-error" role="alert">
              Nome da Categoria é obrigatório
            </small>
          </div>
          <div class="p-field p-col-12 p-mt-4">
            <span class="p-float-label">
              <Textarea
                :id="'description'"
                :name="'description'"
                autocomplete="off"
                v-model="form.description"
                rows="2"
                autoResize
                class="form-control"
              />
              <label for="description">Descrição</label>
            </span>
          </div>
          <div class="p-field p-col-12 p-mt-4">
            <label for="parent_category" style="font-size: 12px; color: #888; display: block; margin-bottom: 4px;">Categoria-Pai</label>
            <div class="p-inputgroup">
              <InputText
                id="parent_category"
                name="parent_category"
                :value="selectedParentCategoryName"
                :disabled="true"
                placeholder="Nível Superior (Principal)"
              />
              <Button
                icon="pi pi-search"
                class="p-button-secondary"
                @click="showCategoryDialog"
                type="button"
                v-tooltip="'Escolher Categoria-Pai'"
              />
              <Button
                v-if="form.parent_id"
                icon="pi pi-times"
                class="p-button-danger"
                @click="clearSelectedParentCategory"
                type="button"
                v-tooltip="'Limpar Categoria-Pai'"
              />
            </div>
          </div>
          <div class="p-field p-col-12 p-mt-4">
            <div>Ativo</div>
            <InputSwitch
              v-model="form.active"
              :name="'active'"
              :id="'active'"
            />
          </div>
          <div class="p-field p-col-12 p-mt-4">
            <label style="font-weight: 600; display: block; margin-bottom: 6px;">Imagem da Categoria</label>
            <div v-if="form.images && form.images.length > 0" class="p-d-flex p-flex-wrap p-ai-center p-jc-start">
              <AuthenticatedImage
                :file="form.images[0]"
                alt="Imagem da Categoria"
                height="100"
                class="p-mr-3"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-sm p-button-danger"
                @click="removeCategoryImage"
                type="button"
                v-tooltip="'Remover Imagem'"
                style="width: auto;"
              />
            </div>
            <div v-else>
              <FileUpload
                ref="categoryImage"
                name="categoryImage[]"
                accept="image/gif,image/jpg,image/jpeg,image/pjpeg,image/png,image/x-png"
                invalidFileTypeMessage="Ficheiro inválido. Apenas aceita GIF, JPG, JPEG ou PNG"
                :previewWidth="100"
                :multiple="false"
                :fileLimit="1"
                :showUploadButton="false"
                :showCancelButton="false"
              >
                <template #empty>
                  <div class="drag-drop-files">
                    <p>Arraste a imagem da categoria ou clique para selecionar</p>
                  </div>
                </template>
              </FileUpload>
            </div>
          </div>
        </div>
      </form>
      <template #footer>
        <Button
          :label="'Guardar'"
          icon="pi pi-save"
          class="p-button-sm p-button-success"
          @click="save()"
        />
        <Button
          :label="'Cancelar'"
          icon="pi pi-times"
          class="p-button-sm p-button-danger"
          @click="hideForm()"
        />
      </template>
    </Dialog>
    <Dialog
      :visible.sync="categoryDialog.show"
      :containerStyle="{ width: $isMobile() ? '100vw' : '30vw' }"
      :modal="true"
      header="Selecionar Categoria-Pai"
    >
      <div style="max-height: 50vh; overflow-y: auto;">
        <Tree
          :value="parentCategoriesTree"
          selectionMode="single"
          :selectionKeys.sync="categoryDialog.selectedKey"
          @node-select="onCategorySelect"
        >
          <template #default="slotProps">
            <span
              :class="{
                'category-node-selected':
                  categoryDialog.tempSelectedNode &&
                  categoryDialog.tempSelectedNode.key === slotProps.node.key,
              }"
              class="category-node"
            >
              <span>{{ slotProps.node.label }}</span>
            </span>
          </template>
        </Tree>
      </div>
      <template #footer>
        <Button
          label="Confirmar"
          icon="pi pi-check"
          class="p-button-sm p-button-success"
          @click="confirmCategorySelection"
          :disabled="!categoryDialog.tempSelectedNode"
        />
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-sm p-button-danger"
          @click="categoryDialog.show = false"
        />
      </template>
    </Dialog>
  </div>
</template>
<script>
import "vue-loading-overlay/dist/vue-loading.css";
import sparsService from "../../services/spars.service";
import AuthenticatedImage from "../../components/elements/AuthenticatedImage.vue";
export default {
  name: "SparsCategories",
  components: {
    AuthenticatedImage,
  },
  data() {
    return {
      loading: false,
      list: [],
      form: {
        show: false,
        id: null,
        name: null,
        description: null,
        active: null,
        parent_id: null,
        images: [],
      },
      filters: {
        name: "",
      },
      categoryDialog: {
        show: false,
        selectedKey: {},
        tempSelectedNode: null,
      },
      importBatch: {
        storageKey: "sparsCategories.importBatchId",
        id: null,
        status: null,
        pollTimer: null,
        inFlight: false,
        cancelling: false,
      },
      destroyed: false,
    };
  },
  computed: {
    importBatchActive() {
      return Boolean(
        this.importBatch.id &&
          (!this.importBatch.status ||
            (!this.importBatch.status.finished &&
              !this.importBatch.status.cancelled))
      );
    },
    importBatchStatusClass() {
      const status = this.importBatch.status;
      if (!status) return "";
      if (status.cancelled) return "import-status-failed";
      if (!status.finished) return "import-status-pending";
      return status.failed_jobs > 0
        ? "import-status-failed"
        : "import-status-completed";
    },
    importBatchStatusLabel() {
      const status = this.importBatch.status;
      if (!status) return "";
      if (status.cancelled) {
        return "Importação cancelada";
      }
      if (!status.finished) {
        return `A importar categorias: ${status.processed_jobs}/${status.total_jobs}`;
      }
      if (status.failed_jobs > 0) {
        return `Importação concluída com ${status.failed_jobs} falha(s)`;
      }
      return "Importação de categorias concluída";
    },
    treeValue() {
      return this.buildTree(this.list);
    },
    selectedParentCategoryName() {
      if (!this.form.parent_id) return "Nível Superior (Principal)";
      const cat = this.list.find((c) => c.id === this.form.parent_id);
      return cat ? cat.name : "Nível Superior (Principal)";
    },
    parentCategoriesTree() {
      let excludedIds = [];
      if (this.form.id) {
        const getDescendants = (id) => {
          let des = [id];
          const children = this.list.filter((c) => c.parent_id === id);
          children.forEach((child) => {
            des = des.concat(getDescendants(child.id));
          });
          return des;
        };
        excludedIds = getDescendants(this.form.id);
      }
      const availableList = this.list.filter(
        (c) => !excludedIds.includes(c.id) && c.active == 1
      );
      return this.buildTree(availableList);
    },
  },
  async created() {
    this.loading = true;
    this.list = (await sparsService.getCategories()) || [];
    this.restoreImportBatch();
    this.loading = false;
  },
  deactivated() {
    this.hideForm();
    if (this.categoryDialog) this.categoryDialog.show = false;
    document.body.classList.remove('p-overflow-hidden');
  },
  beforeDestroy() {
    this.destroyed = true;
    this.stopImportBatchPolling();
    document.body.classList.remove('p-overflow-hidden');
  },
  methods: {
    buildTree(list) {
      const map = {};
      const roots = [];

      list.forEach((item) => {
        map[item.id] = {
          key: String(item.id),
          label: item.name + ' (#' + String(item.api_id) + ')',
          data: item,
          children: []
        };
      });

      list.forEach((item) => {
        const mapped = map[item.id];
        if (item.parent_id && map[item.parent_id]) {
          map[item.parent_id].children.push(mapped);
        } else {
          roots.push(mapped);
        }
      });

      const clearEmptyChildren = (node) => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          node.children.forEach(clearEmptyChildren);
        }
      };

      roots.forEach(clearEmptyChildren);
      return roots;
    },
    showCategoryDialog() {
      const selectedId = this.form.parent_id;
      if (selectedId) {
        this.categoryDialog.selectedKey = { [selectedId]: true };
        const found = this.list.find((c) => c.id === selectedId);
        if (found) {
          this.categoryDialog.tempSelectedNode = {
            key: String(found.id),
            label: found.name,
            data: found,
          };
        } else {
          this.categoryDialog.tempSelectedNode = null;
        }
      } else {
        this.categoryDialog.selectedKey = {};
        this.categoryDialog.tempSelectedNode = null;
      }
      this.categoryDialog.show = true;
    },
    onCategorySelect(node) {
      this.categoryDialog.tempSelectedNode = node;
    },
    confirmCategorySelection() {
      if (this.categoryDialog.tempSelectedNode) {
        this.form.parent_id = parseInt(this.categoryDialog.tempSelectedNode.key);
      }
      this.categoryDialog.show = false;
    },
    clearSelectedParentCategory() {
      this.form.parent_id = null;
    },
    getParentName(parentId) {
      if (!parentId) return "Nível Superior (Principal)";
      const parent = this.list.find((item) => item.id === parentId);
      return parent ? parent.name : "Nível Superior (Principal)";
    },
    hideForm() {
      this.$validator.pause();
      this.$validator.reset();
      return (this.form = {
        show: false,
        id: null,
        name: null,
        description: null,
        active: true,
        parent_id: null,
        images: [],
      });
    },
    addNew() {
      return (this.form = {
        show: true,
        id: null,
        name: null,
        description: null,
        active: true,
        parent_id: null,
        images: [],
      });
    },
    edit(data) {
      return (this.form = {
        show: true,
        id: data.id,
        name: data.name,
        description: data.description || null,
        active: data.active == 1 ? true : false,
        parent_id: data.parent_id,
        images: data.images || [],
        data: data,
      });
    },
    save() {
      this.$validator.validateAll().then((isValid) => {
        if (!isValid) {
          return;
        }
        this.loading = true;
        if (this.form.id) {
          return this.saveEdit();
        }
        return this.saveNew();
      });
    },
    getFormParams() {
      const categoryImageRef = this.$refs.categoryImage;
      const hasFile = categoryImageRef && categoryImageRef.files && categoryImageRef.files.length > 0;

      if (hasFile) {
        let formData = new FormData();
        formData.append("name", this.form.name || "");
        if (this.form.description !== null && this.form.description !== undefined) {
          formData.append("description", this.form.description);
        }
        formData.append("active", this.form.active ? 1 : 0);
        if (this.form.parent_id !== null && this.form.parent_id !== undefined) {
          formData.append("parent_id", this.form.parent_id);
        }
        formData.append("image", categoryImageRef.files[0]);
        return formData;
      } else {
        return {
          name: this.form.name,
          description: this.form.description,
          active: this.form.active ? 1 : 0,
          parent_id: this.form.parent_id,
        };
      }
    },
    saveNew() {
      this.loading = true;

      return sparsService
        .newCategory(this.getFormParams())
        .then((response) => {
          this.loading = false;
          if (!response) {
            return this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail: "Erro ao adicionar Categoria",
              life: 3000,
            });
          }
          this.list.push(response);
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Categoria adicionada com sucesso",
            life: 3000,
          });
          return this.hideForm();
        });
    },
    saveEdit() {
      this.loading = true;
      return sparsService
        .updateCategory(this.form.id, this.getFormParams())
        .then((response) => {
          this.loading = false;
          if (!response) {
            return this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail: "Erro ao atualizar Categoria",
              life: 3000,
            });
          }
          const index = this.list.findIndex((item) => item.id === this.form.id);
          if (index !== -1) {
            this.$set(this.list, index, response);
          }
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Categoria atualizada com sucesso",
            life: 3000,
          });
          return this.hideForm();
        });
    },
    removeCategoryImage() {
      this.$confirm.require({
        header: "Remover Imagem",
        message: "Tem a certeza que pretende remover a imagem desta categoria?",
        icon: "pi pi-question-circle",
        acceptLabel: "Sim",
        acceptIcon: "pi pi-check",
        acceptClass: "p-button-success p-button p-component",
        rejectLabel: "Não",
        rejectIcon: "pi pi-times",
        rejectClass: "p-button-danger p-button p-component",
        accept: () => {
          this.loading = true;
          return sparsService
            .removeCategoryImage(this.form.id)
            .then((response) => {
              if (!response) {
                this.$toast.add({
                  severity: "error",
                  summary: "Erro",
                  detail: "Ocorreu um erro ao remover a imagem.",
                  life: 3000,
                });
                return;
              }
              this.$toast.add({
                severity: "success",
                summary: "Sucesso",
                detail: "Imagem removida com sucesso",
                life: 3000,
              });
              this.form.images = [];
              const index = this.list.findIndex((item) => item.id === this.form.id);
              if (index !== -1) {
                this.$set(this.list, index, response);
              }
            })
            .catch((error) => {
              console.error(error);
              this.$toast.add({
                severity: "error",
                summary: "Erro",
                detail: "Erro de rede ao remover a imagem.",
                life: 3000,
              });
            })
            .finally(() => {
              this.loading = false;
            });
        },
        reject: () => {
          return;
        },
      });
    },
    restoreImportBatch() {
      try {
        const batchId = localStorage.getItem(this.importBatch.storageKey);
        if (batchId) {
          this.importBatch.id = batchId;
          this.loadImportBatchStatus();
        }
      } catch (error) {
        console.error(error);
      }
    },
    stopImportBatchPolling() {
      if (this.importBatch.pollTimer) {
        clearTimeout(this.importBatch.pollTimer);
        this.importBatch.pollTimer = null;
      }
    },
    scheduleImportBatchPoll() {
      this.stopImportBatchPolling();
      if (this.importBatchActive && !this.destroyed) {
        this.importBatch.pollTimer = setTimeout(() => {
          this.loadImportBatchStatus();
        }, 3000);
      }
    },
    async loadImportBatchStatus() {
      if (!this.importBatch.id || this.importBatch.inFlight || this.destroyed) {
        return;
      }
      const batchId = this.importBatch.id;
      this.importBatch.inFlight = true;
      try {
        const status = await sparsService.getCategoryImportBatch(batchId);
        if (this.destroyed || this.importBatch.id !== batchId) {
          return;
        }
        this.importBatch.status = status;
        if (status) {
          if (!status.finished && !status.cancelled) {
            this.scheduleImportBatchPoll();
          } else {
            try {
              localStorage.removeItem(this.importBatch.storageKey);
            } catch (e) {
              console.error(e);
            }
            this.refreshCategories();
            if (status.finished && status.failed_jobs === 0) {
              this.$toast.add({
                severity: "success",
                summary: "Sucesso",
                detail: "Categorias importadas com sucesso",
                life: 3000,
              });
            } else if (status.failed_jobs > 0) {
              this.$toast.add({
                severity: "error",
                summary: "Aviso",
                detail: "A importação de categorias terminou com falhas.",
                life: 4000,
              });
            }
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.importBatch.id = null;
          this.importBatch.status = null;
          try {
            localStorage.removeItem(this.importBatch.storageKey);
          } catch (e) {
            console.error(e);
          }
        } else if (!this.destroyed && this.importBatch.id === batchId) {
          this.scheduleImportBatchPoll();
        }
      } finally {
        this.importBatch.inFlight = false;
      }
    },
    confirmCancelImportBatch() {
      if (!this.importBatchActive || this.importBatch.cancelling) {
        return;
      }
      this.$confirm.require({
        message: "Tem a certeza que deseja cancelar a importação de categorias?",
        header: "Cancelar Importação",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim, Cancelar",
        acceptClass: "p-button-danger",
        rejectLabel: "Não",
        accept: async () => {
          this.importBatch.cancelling = true;
          try {
            await sparsService.cancelCategoryImportBatch(this.importBatch.id);
            this.$toast.add({
              severity: "info",
              summary: "Cancelamento",
              detail: "Pedido de cancelamento enviado.",
              life: 3000,
            });
            await this.loadImportBatchStatus();
          } catch (error) {
            console.error(error);
            this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail: "Não foi possível cancelar a importação.",
              life: 3000,
            });
          } finally {
            this.importBatch.cancelling = false;
          }
        },
      });
    },
    async refreshCategories() {
      try {
        const categories = await sparsService.getCategories();
        if (categories) {
          this.list = categories;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async importCategories() {
      this.$confirm.require({
        message:
          "Tem a certeza que deseja importar todas as categorias do PrestaShop? Este processo pode atualizar nomes, estados e a estrutura de árvore de categorias locais.",
        header: "Importar Categorias",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim",
        rejectLabel: "Não",
        accept: async () => {
          this.loading = true;
          const response = await sparsService.importCategories();
          this.loading = false;
          if (!response || !response.batch_id) {
            this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail:
                (response && response.message) ||
                "Erro ao iniciar importação de categorias",
              life: 3000,
            });
            return;
          }
          this.importBatch.id = response.batch_id;
          this.importBatch.status = null;
          try {
            localStorage.setItem(
              this.importBatch.storageKey,
              response.batch_id
            );
          } catch (e) {
            console.error(e);
          }
          this.$toast.add({
            severity: "info",
            summary: "Em fila",
            detail:
              response.message ||
              "Importação de categorias colocada em fila com sucesso",
            life: 3000,
          });
          this.loadImportBatchStatus();
        },
      });
    },
    confirmDelete(data) {
      this.$confirm.require({
        message: `Tem a certeza que deseja eliminar a categoria "${data.name}"?`,
        header: 'Confirmar Eliminação',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim',
        rejectLabel: 'Não',
        accept: () => {
          this.executeDelete(data.id, false);
        }
      });
    },
    executeDelete(id, force = false) {
      this.loading = true;
      sparsService.deleteCategory(id, force).then((response) => {
        this.loading = false;
        if (!response) {
          this.$toast.add({
            severity: "error",
            summary: "Erro",
            detail: "Erro ao eliminar categoria",
            life: 3000,
          });
          return;
        }

        if (response.requires_confirmation) {
          let warnMessage = '';
          if (response.children_count > 0 && response.products_count > 0) {
            warnMessage = `Esta categoria tem ${response.children_count} subcategoria(s) e ${response.products_count} produto(s) associado(s). Se continuar, as subcategorias serão eliminadas e todos os produtos serão desassociados. Deseja forçar a eliminação?`;
          } else if (response.children_count > 0) {
            warnMessage = `Esta categoria tem ${response.children_count} subcategoria(s). Se continuar, as subcategorias serão eliminadas. Deseja forçar a eliminação?`;
          } else {
            warnMessage = `Esta categoria tem ${response.products_count} produto(s) associado(s). Se continuar, os produtos serão desassociados. Deseja forçar a eliminação?`;
          }

          this.$confirm.require({
            message: warnMessage,
            header: 'Aviso de Relações Existentes',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim, forçar',
            rejectLabel: 'Não',
            accept: () => {
              this.executeDelete(id, true);
            }
          });
          return;
        }

        this.list = response;
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: "Categoria eliminada com sucesso",
          life: 3000,
        });
      });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.import-status {
  display: inline-flex;
  align-items: center;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.65rem;
}

.import-status-pending {
  background: #fff3cd;
  color: #856404;
}

.import-status-completed {
  background: #d4edda;
  color: #155724;
}

.import-status-failed {
  background: #f8d7da;
  color: #721c24;
}

.category-node {
  padding: 6px 12px;
  border-radius: 4px;
  display: inline-block;
  width: 100%;
}

.category-node-selected {
  background-color: #1b80d0 !important;
  color: #ffffff !important;
}

.category-node-selected span {
  color: #ffffff !important;
}
</style>
